/**
 * CastleAnimation — regal castle 🏰 with 3 staggered firework bursts above.
 * Each firework emits 8 radial particles (colored per burst: gold, rose, blue)
 * that fade and drift down slightly, mimicking a real skyfall. Castle bobs
 * with a golden royal aura.
 */
function fireworkParticles(color, delay, cx, cy) {
  return new Array(10).fill(0).map((_, i) => {
    const angle = (i * 360) / 10;
    const dx = `${Math.cos((angle * Math.PI) / 180) * 42}px`;
    const dy = `${Math.sin((angle * Math.PI) / 180) * 42 + 18}px`; // +bias down for gravity
    return { color, delay, cx, cy, dx, dy, angle };
  });
}

export default function CastleAnimation({ size = 140 }) {
  // 3 firework bursts at slightly different positions + colors + delays.
  const bursts = [
    ...fireworkParticles("#fbbf24", "0.15s", "30%", "20%"),
    ...fireworkParticles("#f43f5e", "0.4s",  "70%", "15%"),
    ...fireworkParticles("#60a5fa", "0.65s", "50%", "10%"),
  ];
  // Firework "flash" cores — quick bright dot that flashes at t=delay
  const cores = [
    { color: "#fef3c7", delay: "0.1s",  cx: "30%", cy: "20%" },
    { color: "#fecdd3", delay: "0.35s", cx: "70%", cy: "15%" },
    { color: "#dbeafe", delay: "0.6s",  cx: "50%", cy: "10%" },
  ];
  return (
    <div
      className="pointer-events-none"
      style={{ width: size, height: size, position: "relative", overflow: "hidden" }}
    >
      <style>{`
        @keyframes castle-bob {
          0%   { transform: translateY(24px) scale(0.5) rotate(-4deg); opacity: 0; filter: drop-shadow(0 0 0 rgba(251,191,36,0)); }
          25%  { transform: translateY(-4px) scale(1.5) rotate(2deg); opacity: 1; filter: drop-shadow(0 0 34px rgba(251,191,36,0.9)); }
          55%  { transform: translateY(2px) scale(1.35) rotate(-1deg); opacity: 1; filter: drop-shadow(0 0 30px rgba(251,191,36,0.85)); }
          80%  { transform: translateY(0) scale(1.3) rotate(1deg); opacity: 0.95; filter: drop-shadow(0 0 24px rgba(245,158,11,0.75)); }
          100% { transform: translateY(0) scale(1) rotate(0deg); opacity: 0; filter: drop-shadow(0 0 0 rgba(251,191,36,0)); }
        }
        @keyframes castle-firework-particle {
          0%   { transform: translate(0, 0) scale(0.2); opacity: 0; }
          15%  { opacity: 1; transform: translate(calc(var(--dx) * 0.5), calc(var(--dy) * 0.5)) scale(1); }
          70%  { opacity: 0.9; }
          100% { transform: translate(var(--dx, 0), var(--dy, 0)) scale(0.4); opacity: 0; }
        }
        @keyframes castle-firework-core {
          0%   { transform: translate(-50%, -50%) scale(0); opacity: 0; }
          5%   { transform: translate(-50%, -50%) scale(1.8); opacity: 1; }
          25%  { transform: translate(-50%, -50%) scale(1.2); opacity: 0.9; }
          60%  { transform: translate(-50%, -50%) scale(0.6); opacity: 0.4; }
          100% { transform: translate(-50%, -50%) scale(0); opacity: 0; }
        }
      `}</style>

      {/* Firework flash cores */}
      {cores.map((c, i) => (
        <div
          key={`c${i}`}
          style={{
            position: "absolute",
            left: c.cx,
            top: c.cy,
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${c.color}, transparent 70%)`,
            transform: "translate(-50%, -50%)",
            animation: `castle-firework-core 1.4s ease-out ${c.delay} forwards`,
            boxShadow: `0 0 24px ${c.color}`,
          }}
        />
      ))}

      {/* Firework radial particles */}
      {bursts.map((p, i) => (
        <div
          key={`p${i}`}
          style={{
            position: "absolute",
            left: p.cx,
            top: p.cy,
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: p.color,
            boxShadow: `0 0 8px ${p.color}`,
            transform: "translate(-50%, -50%)",
            animation: `castle-firework-particle 1.4s ease-out ${p.delay} forwards`,
            "--dx": p.dx,
            "--dy": p.dy,
          }}
        />
      ))}

      {/* Castle emoji */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          fontSize: size * 0.68,
          lineHeight: 1,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          paddingBottom: "8%",
          animation: "castle-bob 1.4s cubic-bezier(.22,1,.36,1) forwards",
        }}
      >
        🏰
      </div>
    </div>
  );
}
